import { useDisplay } from "vuetify/lib/composables/display.mjs";
import type { HistoryState } from "vue-router";

/**
 * A single segment in an app-bar breadcrumb trail.
 * @property label - Display text.
 * @property to    - Optional route path; when set the segment is rendered as a NuxtLink.
 * @property state - Optional router history state passed alongside the navigation.
 */
export interface BreadcrumbItem {
  label: string;
  to?: string;
  state?: HistoryState;
}

export const useAppBar = () => {
  //
  // Composables
  //

  // Route
  const route = useRoute();

  // Breakpoints
  const { smAndDown } = useDisplay();

  // Localization
  const { t } = useI18n();
  const localePath = useLocalePath();

  //
  // State
  //
  const currentPath = computed(() => route.path);

  /**
   * Breadcrumb trail set by detail-page composables.
   * Each item has a label and an optional navigation target.
   * When non-empty, the app bar title renders as a clickable breadcrumb.
   * Format: [{label: "Channels", to: "/app"}, {label: "Cobas Pure"}]
   */
  const breadcrumbs = useState<BreadcrumbItem[]>("ui.breadcrumbs", () => []);

  //
  // Computed Properties
  //
  const defaultPageTitle = computed(() => {
    // Get the path without locale prefix
    const pathWithoutLocale = route.path.replace(/^\/(en|bg|mk)/, "") || "/";

    // Map routes to translation keys
    const titleMap: Record<string, string> = {
      "/": "components.common.app-bar.page_titles.login",
      "/app": "components.common.app-bar.page_titles.channels",
      "/app/instruments": "components.common.app-bar.page_titles.instruments",
      "/app/validation": "components.common.app-bar.page_titles.validation",
      "/app/about": "components.common.app-bar.page_titles.about",
    };

    // Get title key based on current route
    const titleKey = titleMap[pathWithoutLocale];

    // Dynamic instrument detail page title
    if (!titleKey && pathWithoutLocale.startsWith("/app/instruments/")) {
      return t("components.common.app-bar.page_titles.instruments");
    }

    // Dynamic location sub-route titles
    if (!titleKey && pathWithoutLocale.startsWith("/app/locations/")) {
      return t("components.common.app-bar.page_titles.locations");
    }

    return titleKey ? t(titleKey) : t("components.common.app-bar.page_titles.channels");
  });

  /** Effective page title: breadcrumb labels joined if set, else route-based default */
  const pageTitle = computed(() =>
    breadcrumbs.value.length > 0 ? breadcrumbs.value.map((b) => b.label).join(" / ") : defaultPageTitle.value,
  );

  /**
   * Get app bar extension configuration for current route
   */
  const getExtensionConfig = computed(() => {
    const path = currentPath.value;

    // Exact matches only
    const exactConfigs = new Map<string, { show: boolean; component: string }>([
      [localePath("/app"), { show: false, component: "" }],
      [localePath("/app/instruments"), { show: false, component: "InstrumentsListActions" }],
      [localePath("/app/validation"), { show: smAndDown.value, component: "ValidationExtension" }],
      [localePath("/app/about"), { show: false, component: "" }],
    ]);

    // Check exact match first
    if (exactConfigs.has(path)) {
      return exactConfigs.get(path)!;
    }

    // Strip locale prefix for partial-path checks
    const pathWithoutLocale = path.replace(/^\/(en|bg|mk)/, "") || "/";

    // Test detail pages — tabs and actions live in the page (BaseMasterCard + BaseTabCard)
    if (pathWithoutLocale.match(/^\/app\/instruments\/\d+\/tests\/\d+/)) {
      return { show: false, component: "" };
    }

    // Instrument detail pages — actions live in the master card
    if (pathWithoutLocale.startsWith("/app/instruments/")) {
      return { show: false, component: "" };
    }

    // Default: no extension
    return { show: false, component: "" };
  });

  return {
    pageTitle: readonly(pageTitle),
    breadcrumbs,
    extensionsConfig: getExtensionConfig,
  } as const;
};
